<template>
  <div>
    <b-container class="my-2">
      <b-row>
        <b-col>
          <b-collapse
            class="pt-2"
            :visible="status.showHelp"
            id="collapse-help"
          >
            <b-alert show variant="warning">
              <div>
                <h4 class="font-weight-bold">
                  <!-- <b-icon icon="question-circle"></b-icon>  -->
                  📖️ Explore the Entity Network
                </h4>
                <!-- <p class="font-weight-bold">Let's get started!</p> -->
                <hr />
                ECCE has extracted an entity network based on your annotations.
                Nodes are color-coded by entity type, and the frequency with
                which an entity occurs in the corpus is represented by node
                size. Entities that cooccur in the corpus are connected by
                edges, whose width and opacity corresponds to the edge weight.
                Click a node or edge for further details and to find documents
                the selected entity occurs in. You can navigate the graph using
                panning and zooming.
              </div>
              <b-button class="my-2" variant="primary" @click="closeHelp()"
                >Close Help</b-button
              >
            </b-alert>
          </b-collapse>
        </b-col>
      </b-row>

      <b-row>
        <b-col xl="8" class="py-2 my-auto">
          <!-- Rebuild Graph Button -->

          <b-button
            size="md"
            v-b-tooltip.hover
            title="Rebuild the entity network."
            id="popover-build-button"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="gear"></b-icon>
            Extract Graph
          </b-button>

          <!-- Rebuild Graph Menue -->
          <b-popover
            id="popover-build-content"
            target="popover-build-button"
            triggers="click"
            placement="bottom"
            container="popover-build-button-content"
            ref="popover"
            :show.sync="menues.rebuild.popoverShow"
          >
            <template #title>
              <b-button
                @click="onCloseRebuild()"
                class="close"
                aria-label="Close"
              >
                <span class="d-inline-block" aria-hidden="true">&times;</span>
              </b-button>
              Rebuild Options
            </template>
            <div>
              <!-- Cutoff Parameter -->
              <b-form-group
                label="Cutoff (c)"
                label-for="popover-build-input-c"
                label-cols="4"
                class="mb-1"
                description="Pick a value for c."
                invalid-feedback="This field is required"
              >
                <b-form-input
                  id="popover-build-input-c"
                  size="sm"
                  type="number"
                  v-model="menues.rebuild.options.cutoff"
                ></b-form-input>
              </b-form-group>
              <b-button @click="onOkRebuild()" variant="primary"
                >Build</b-button
              >
            </div>
          </b-popover>

          <b-button
            size="md"
            v-b-tooltip.hover
            title="Display Options"
            id="popover-options-button"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="sliders"></b-icon>
            Display Options
          </b-button>

          <!-- Visual Options Menue -->
          <b-popover
            id="popover-options-content"
            target="popover-options-button"
            triggers="click"
            placement="bottom"
            container="popover-options-button-content"
            ref="popover-options"
            :show.sync="menues.options.popoverShow"
          >
            <template #title>
              <b-button
                @click="onCloseOptions()"
                class="close"
                aria-label="Close"
              >
                <span class="d-inline-block" aria-hidden="true">&times;</span>
              </b-button>
              Display Options
            </template>
            <div>
              <!-- Node Occurrence Threshold-->
              <b-form-group
                label="Occurrence Threshold"
                label-for="popover-options-input-node-threshold"
                label-cols="4"
                class="mb-1"
                description="Display only entities appearing at least this often."
                invalid-feedback="This field is required"
              >
                <b-form-input
                  id="popover-options-input-node-threshold"
                  v-model="menues.options.options.nodeThreshold"
                  type="range"
                  min="0"
                  :max="maxNodeValue"
                  @mousemove="filterNetwork()"
                ></b-form-input>
                Value: {{ menues.options.options.nodeThreshold }}
              </b-form-group>

              <!-- Edge weight Threshold-->
              <b-form-group
                label="Edge Weight Threshold"
                label-for="popover-options-input-edge-threshold"
                label-cols="4"
                class="mb-1"
                description="Display only edges with a weight of at least this value."
                invalid-feedback="This field is required"
              >
                <b-form-input
                  id="popover-options-input-edge-threshold"
                  v-model="menues.options.options.edgeThreshold"
                  type="range"
                  min="0"
                  :max="Math.round(maxEdgeValue)"
                  @mousemove="filterNetwork()"
                ></b-form-input>
                Value: {{ menues.options.options.edgeThreshold }}
              </b-form-group>

              <!-- Entity Types -->
              <b-form-group
                label="Choose entity types to display:"
                label-for="popover-options-input-entity-types"
              >
                <b-form-tags
                  id="popover-options-input-entity-types"
                  v-model="menues.options.options.typesSelected"
                  no-outer-focus
                  class="mb-2"
                >
                  <template v-slot="{ tags, disabled, addTag, removeTag }">
                    <ul
                      v-if="tags.length > 0"
                      class="list-inline d-inline-block mb-2"
                    >
                      <li
                        v-for="tag in tags"
                        :key="tag"
                        class="list-inline-item"
                      >
                        <b-form-tag
                          @remove="removeTag(tag)"
                          :title="tag"
                          :disabled="disabled"
                          variant="secondary"
                          >{{ tag }}</b-form-tag
                        >
                      </li>
                    </ul>

                    <b-dropdown
                      size="sm"
                      variant="outline-secondary"
                      block
                      menu-class="w-100"
                    >
                      <template #button-content> Add Entity Type </template>
                      <b-dropdown-item-button
                        v-for="option in availableOptions"
                        :key="option"
                        @click="onOptionClick({ option, addTag })"
                      >
                        {{ option }}
                      </b-dropdown-item-button>
                      <b-dropdown-text v-if="availableOptions.length === 0">
                        There are no further entity types available to select.
                      </b-dropdown-text>
                    </b-dropdown>
                  </template>
                </b-form-tags>
              </b-form-group>

              <hr />
              <!-- Top n Nodes to display -->
              <b-form-group
                label="Number of Nodes"
                label-for="popover-options-input-n"
                label-cols="4"
                class="mb-1"
                description="Nodes with the top n highest values are displayed."
                invalid-feedback="This field is required"
              >
                <b-form-input
                  id="popover-options-input-n"
                  size="md"
                  type="number"
                  v-model="menues.options.options.n"
                ></b-form-input>
              </b-form-group>
              <b-button @click="updateGraph()" variant="primary"
                >Redraw</b-button
              >
            </div>
          </b-popover>

          <b-button
            size="md"
            v-b-tooltip.hover
            title="List all documents in the corpus."
            @click="filterDocuments([])"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="skip-start"></b-icon>
            Reset Filter
          </b-button>

          <!-- <b-button
            v-b-tooltip.hover
            title="Refresh Word Network"
            v-on:click="updateGraph()"
          >
            <b-icon icon="arrow-repeat"></b-icon>
          </b-button> -->
          <b-button
            v-b-tooltip.hover
            size="md"
            title="Help"
            @click="toogleHelp()"
            variant="secondary"
            class="shadow my-1"
          >
            <b-icon icon="question-circle"></b-icon>
          </b-button>
        </b-col>
        <!-- <b-col> </b-col> -->
        <b-col xl="4" class="py-2 my-auto">
          <b-button
            size="md"
            v-bind:to="'documents'"
            variant="primary"
            class="shadow my-1"
          >
            <b-icon icon="chevron-compact-left"></b-icon>
            Documents</b-button
          >
          <b-button
            size="md"
            v-bind:to="'entities'"
            variant="primary"
            class="shadow my-1"
          >
            <b-icon icon="chevron-compact-left"></b-icon>
            Annotations</b-button
          >
        </b-col>
      </b-row>
    </b-container>
    <b-container class="my-2" fluid>
      <!-- Conditional Rendering of Graph-->

      <b-row align-h="center">
        <b-col xl="8">
          <b-overlay :show="status.showSpinner" rounded="sm">
            <div id="chart" style="height: 75vh; overflow: hidden"></div>
          </b-overlay>
        </b-col>
        <b-col md>
          <listDocuments :corpus_id="corpus_id" :documentIds="documentSelected">
          </listDocuments>
        </b-col>
      </b-row>
    </b-container>

    <!-- Tooltip displaying Entity Details-->
    <tooltipNode
      :corpus_id="corpus_id"
      :node="graph.selected.node"
      @documents_selected="filterDocuments"
    ></tooltipNode>

    <!-- Tooltip displaying Edge Details-->
    <tooltipEdge
      :corpus_id="corpus_id"
      :edge="graph.selected.edge"
    ></tooltipEdge>
  </div>
</template>

<script>
import * as d3 from "d3";
import requestsMixin from "../mixins/requestsMixin";
import graphMixin from "../mixins/graphMixin";
import tooltipNode from "./exploreGraphTooltipNode.vue";
import tooltipEdge from "./exploreGraphTooltipEdge.vue";
import constantsMixin from "../mixins/constantsMixin";

import listDocuments from "./listDocuments.vue";

export default {
  name: "component-explore-graph",
  mixins: [requestsMixin, graphMixin, constantsMixin],
  props: { corpus_id: { type: String } },
  components: { tooltipNode, tooltipEdge, listDocuments },
  data() {
    return {
      documentSelected: [],

      status: {
        showSpinner: false,
        showHelp: false,
      },
      // Variables used for
      // the rebuild menue
      menues: {
        rebuild: {
          options: {
            cutoff: 2,
          },
          popoverShow: false,
        },
        options: {
          options: {
            nodeThreshold: 0,
            edgeThreshold: 0,
            n: 50,
            types: [
              "ORG",
              "CARDINAL",
              "DATE",
              "GPE",
              "PERSON",
              "MONEY",
              "PRODUCT",
              "TIME",
              "PERCENT",
              "WORK_OF_ART",
              "QUANTITY",
              "NORP",
              "LOC",
              "EVENT",
              "ORDINAL",
              "FAC",
              "LAW",
              "LANGUAGE",
            ],
            typesSelected: [
              "ORG",
              "GPE",
              "PERSON",
              "WORK_OF_ART",
              "NORP",
              "LOC",
            ],
          },
          popoverShow: false,
        },
      },
      graph: {
        simulation: null,
        nodes: [],
        links: [],
        selected: {
          node: {},
          edge: { source: {}, target: {} },
        },
      },
    };
  },
  methods: {
    /* ---------------------------- Filter Documents ---------------------------- */

    // Filter documents shown
    filterDocuments(documentIds) {
      this.documentSelected = documentIds;
    },

    /* ----------------------- Navigating the Application ----------------------- */

    // Functions for navigating
    goToExtractEntities() {
      this.$router.push({
        name: "extract_entities",
        params: { reparseDocuments: false },
      });
    },

    /* --------------------------------- Options -------------------------------- */

    // Methods used to controle
    // the rebuild menue
    onCloseRebuild() {
      this.menues.rebuild.popoverShow = false;
    },
    onOkRebuild() {
      this.fetchGraph();
      this.menues.rebuild.popoverShow = false;
    },

    // Methods used to controle
    // the display options menue
    onCloseOptions() {
      this.menues.options.popoverShow = false;
    },
    onOkOptions() {
      this.filterNetwork();
    },

    // Add selected type to list of selected
    // types to search for in corpus
    onOptionClick({ option, addTag }) {
      addTag(option);
    },

    /* -------------------------- Fetch and Draw Graph -------------------------- */

    // refreshGraphData(corpus_id) {
    //   if (!!corpus_id) {
    //     // // Start spinner
    //     // new Promise((resolve) => {
    //     //   this.status.showSpinner = true; // Show spinner
    //     //   resolve(true);
    //     // })
    //     //   // Fetch meta information about corpus
    //     //   .then(() => {
    //     //     let document_count;
    //     //     if (this.documentIds.length > 0) {
    //     //       document_count = [...new Set(this.documentIds)].length; // count of selected documents
    //     //     } else {
    //     //       document_count = this.fetchCorpusMetaData(corpus_id); // count of all documents
    //     //     }
    //     //     return document_count;
    //     //   })
    //     //   .then((document_count) => {
    //     //     this.pagination.documents = document_count;
    //     //   })
    //     //   // Fetch documents in corpus
    //     //   .then(() => {
    //     //     return this.fetchDocumentsById(
    //     //       corpus_id,
    //     //       this.documentIds,
    //     //       this.pagination.currentPage,
    //     //       this.pagination.perPage
    //     //     );
    //     //   })
    //     //   .then((response) => {
    //     //     this.documents = response;
    //     //   })
    //     //   // Stop spinner
    //     //   .finally(() => {
    //     //     this.status.showSpinner = false; // Hide spinner
    //     //   })
    //     //   // Shwow error message
    //     //   .catch((error) => {
    //     //     console.log(error);
    //     //     this.showToast(
    //     //       String(error.message),
    //     //       String(error.cause),
    //     //       "danger"
    //     //     );
    //     //   });
    //   }
    // },

    updateGraph() {
      this.simulation = null; //reset simulation
      // Node group coloring
      //let colors = d3.schemeTableau10; // color palette used
      //let entityTypes = [...new Set(this.graph.nodes.map((d) => d.group))];
      let entityTypeScale = this.entityColorScale; //d3.scaleOrdinal(entityTypes, colors);

      // graph will be displayed here
      let graphDiv = d3.select("#chart");

      // deepcopy nodes and edges
      let nodes = JSON.parse(JSON.stringify(this.graph.nodes));
      let edges = JSON.parse(JSON.stringify(this.graph.links));

      // filter nodes and edges
      let n = this.menues.options.options.n;

      if (n > 0) {
        var [nodesToDraw, edgesToDraw] = this.filterTopNodes(nodes, edges, n);
      }

      /* ----------------------------  Define scales ---------------------------- */

      let maxNodeValue = Math.max.apply(
        Math,
        nodesToDraw.map(function (d) {
          return d.value;
        })
      );

      let minNodeValue = Math.min.apply(
        Math,
        nodesToDraw.map(function (d) {
          return d.value;
        })
      );

      let scaleNodeValues = d3
        .scaleLinear()
        .domain([minNodeValue, maxNodeValue])
        .range([0, 15]);

      let maxEdgeValue = Math.max.apply(
        Math,
        edgesToDraw.map(function (d) {
          return d.value;
        })
      );

      let minEdgeValue = Math.min.apply(
        Math,
        edgesToDraw.map(function (d) {
          return d.value;
        })
      );

      let scaleEdgeValues = d3
        .scaleSqrt()
        .domain([minEdgeValue, maxEdgeValue])
        .range([0, 3]);

      let scaleEdgeOpacity = d3
        .scaleLog()
        .domain([minEdgeValue, maxEdgeValue])
        .range([0, 1]);
      /* ------------------------------- Draw Graph ------------------------------- */

      // draw graph
      this.drawGraph(nodesToDraw, edgesToDraw, this.graph.selected, {
        containerElement: graphDiv,
        chargeStrength: -400,
        nodeFillColorScale: entityTypeScale,
        nodeLabel: (d) => d.name,
        nodeLabelFontSize: (d) => Math.max(8, scaleNodeValues(d.value)) + "px",
        nodeRadius: (d) => Math.max(5, scaleNodeValues(d.value)),
        linkStrokeOpacity: (l) => Math.max(scaleEdgeOpacity(l.value), 0.2),
        linkStrokeWidth: (l) => Math.max(scaleEdgeValues(l.value), 0.2),
      });
    },

    /* ---------------------------------- Help ---------------------------------- */

    toogleHelp() {
      this.status.showHelp = !this.status.showHelp;
      this.$cookies.set(
        "exploreGraph.showHelp",
        this.status.showHelp === true ? 1 : 0,
        Infinity
      );
    },

    openHelp() {
      this.status.showHelp = true;
      this.$cookies.set("exploreGraph.showHelp", 1, Infinity);
    },

    closeHelp() {
      this.status.showHelp = false;
      this.$cookies.set("exploreGraph.showHelp", 0, Infinity);
    },
  },
  watch: {
    corpus_id(value) {
      this.fetchGraph();
    },
    "menues.options.options.typesSelected"(value) {
      this.filterNetwork();
    },
  },
  computed: {
    // Compute available types for parsing menue
    // by filtering out already selected options
    availableOptions() {
      const types = this.menues.options.options.types;
      const typesSelected = this.menues.options.options.typesSelected;
      const typesAvailable = types.filter(
        (type) => typesSelected.indexOf(type) === -1
      );
      return typesAvailable;
    },
    maxNodeValue: function () {
      return Math.max.apply(
        Math,
        this.graph.nodes.map(function (d) {
          return d.value;
        })
      );
    },
    maxEdgeValue: function () {
      return Math.max.apply(
        Math,
        this.graph.links.map(function (d) {
          return d.value;
        })
      );
    },
  },
  created() {
    this.fetchGraph();

    /* ---------------------- Determine help window status ---------------------- */

    // Get previous state of the help window
    var showHelpPrevious = this.$cookies.get("exploreGraph.showHelp");

    // Previous help window status is "open"
    if (showHelpPrevious == 1) {
      this.openHelp();
    }

    // No cooky set (first visit or disabled);
    // If the corpus_id cooky is not set at this point,
    // it is assumed that cookies are disabled.
    if (
      showHelpPrevious === null &&
      !(this.$cookies.get("corpus_id") === null)
    ) {
      this.openHelp();
    }
  },
};
</script>

<style scoped>
</style>


// WEBPACK FOOTER //
// src/components/exploreGraph.vue